import { safeStorage, safeSessionStorage, isStorageAvailable } from '@/utils/safariStorage';
import { isNativePlatform } from '@/utils/nativePlugins';

/**
 * Secure authentication storage implementation
 * This replaces the default storage with enhanced security measures
 * Includes Safari Private Mode fallbacks
 */

interface AuthStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

// Track if we've warned about storage issues
let hasWarnedAboutStorage = false;

// Type-specific storage keys for Remember Me
const REMEMBER_ME_KEY_SELLER = 'godyspo_auth_seller_remember_me';
const REMEMBER_ME_KEY_BUYER = 'godyspo_auth_buyer_remember_me';
const REMEMBERED_EMAIL_KEY_SELLER = 'godyspo_auth_seller_remembered_email';
const REMEMBERED_EMAIL_KEY_BUYER = 'godyspo_auth_buyer_remembered_email';

/**
 * Creates a Safari-safe Storage proxy that implements the full Storage interface
 * but uses our safe wrappers internally for try-catch protection
 */
const createSafeStorageProxy = (isLocalStorage: boolean): Storage => {
  const wrapper = isLocalStorage ? safeStorage : safeSessionStorage;
  const getRawStorage = () => isLocalStorage ? localStorage : sessionStorage;
  
  return {
    get length(): number {
      try { return getRawStorage().length; } 
      catch { return 0; }
    },
    key(index: number): string | null {
      try { return getRawStorage().key(index); } 
      catch { return null; }
    },
    getItem: (key: string) => wrapper.getItem(key),
    setItem: (key: string, value: string) => wrapper.setItem(key, value),
    removeItem: (key: string) => wrapper.removeItem(key),
    clear: () => wrapper.clear()
  };
};

/**
 * Get the appropriate storage type based on remember me preference
 * Uses safe storage wrappers for Safari compatibility
 * Checks both seller and buyer preferences for Supabase client compatibility
 */
export const getStorageType = (): Storage => {
  // Check storage availability
  if (!isStorageAvailable() && !hasWarnedAboutStorage) {
    console.warn('localStorage not available, using fallback storage');
    hasWarnedAboutStorage = true;
  }
  
  // NATIVE APPS: Always use localStorage for session persistence
  // Mobile users expect to stay logged in when reopening the app
  // WebView sessionStorage is destroyed when the app closes
  if (isNativePlatform()) {
    return createSafeStorageProxy(true); // Always use localStorage on native
  }
  
  // WEB BROWSERS: Respect "Remember Me" preference
  const sellerRememberMe = safeStorage.getItem(REMEMBER_ME_KEY_SELLER);
  const buyerRememberMe = safeStorage.getItem(REMEMBER_ME_KEY_BUYER);
  const shouldPersist = sellerRememberMe === 'true' || buyerRememberMe === 'true';
  
  // Return SafeStorageProxy instead of raw storage for Safari compatibility
  return shouldPersist 
    ? createSafeStorageProxy(true)   // Safe localStorage proxy
    : createSafeStorageProxy(false); // Safe sessionStorage proxy
};

class DynamicAuthStorage implements AuthStorage {
  private readonly keyPrefix = 'godyspo_auth_';

  private getStorage(): Storage {
    return getStorageType();
  }

  getItem(key: string): string | null {
    try {
      const storage = this.getStorage();
      const item = storage.getItem(this.keyPrefix + key);
      
      // Additional validation for auth tokens
      if (key.includes('token') && item) {
        try {
          const parsed = JSON.parse(item);
          // Validate token structure
          if (parsed && typeof parsed === 'object' && parsed.access_token) {
            return item;
          }
          // Remove invalid token
          this.removeItem(key);
          return null;
        } catch {
          // Remove corrupted token
          this.removeItem(key);
          return null;
        }
      }
      
      return item;
    } catch (error) {
      console.error('Auth storage getItem error:', error);
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      const storage = this.getStorage();
      
      // Never log auth tokens or sensitive data
      if (!key.includes('token') && !key.includes('password')) {
        console.debug('Setting auth storage item:', key);
      }
      
      storage.setItem(this.keyPrefix + key, value);
    } catch (error) {
      // Safari Private Mode may throw QuotaExceededError
      console.error('Auth storage setItem error:', error);
      // Try safe storage fallback
      safeStorage.setItem(this.keyPrefix + key, value);
    }
  }

  removeItem(key: string): void {
    try {
      // Use safe storage wrappers for Safari compatibility
      safeStorage.removeItem(this.keyPrefix + key);
      safeSessionStorage.removeItem(this.keyPrefix + key);
    } catch (error) {
      console.error('Auth storage removeItem error:', error);
    }
  }

  // Clean up expired or invalid tokens
  cleanup(): void {
    try {
      const checkStorage = (storage: Storage) => {
        try {
          // Use safe iteration with length/key() instead of Object.keys()
          // Object.keys() doesn't work on SafeStorageProxy and crashes in Safari Private Mode
          const keys: string[] = [];
          for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key) keys.push(key);
          }
          
          keys.forEach(key => {
            if (key.startsWith(this.keyPrefix)) {
              const item = storage.getItem(key);
              if (item) {
                try {
                  const parsed = JSON.parse(item);
                  // Check if token is expired
                  if (parsed.expires_at && parsed.expires_at * 1000 < Date.now()) {
                    storage.removeItem(key);
                  }
                } catch {
                  // Remove corrupted items
                  storage.removeItem(key);
                }
              }
            }
          });
        } catch (storageError) {
          // Storage access failed (Safari Private Mode, etc.)
          console.warn('Storage cleanup skipped due to access error');
        }
      };

      // Use safe storage proxies for Safari compatibility
      try { checkStorage(createSafeStorageProxy(true)); } catch {}
      try { checkStorage(createSafeStorageProxy(false)); } catch {}
    } catch (error) {
      console.error('Auth storage cleanup error:', error);
    }
  }
}

const dynamicAuthStorage = new DynamicAuthStorage();

export const authStorage = dynamicAuthStorage;

/**
 * Set remember me preference for authentication
 * Store in localStorage so it persists across all storage types
 * Uses safe storage for Safari compatibility
 * @param rememberMe - Whether to remember the user
 * @param userType - 'seller' or 'buyer' to use type-specific storage keys
 */
export const setRememberMePreference = (rememberMe: boolean, userType: 'seller' | 'buyer' = 'seller'): void => {
  try {
    const key = userType === 'buyer' ? REMEMBER_ME_KEY_BUYER : REMEMBER_ME_KEY_SELLER;
    safeStorage.setItem(key, rememberMe ? 'true' : 'false');
  } catch (error) {
    console.error('Error setting remember me preference:', error);
  }
};

/**
 * Get remember me preference for authentication
 * Uses safe storage for Safari compatibility
 * @param userType - 'seller' or 'buyer' to use type-specific storage keys
 */
export const getRememberMePreference = (userType: 'seller' | 'buyer' = 'seller'): boolean => {
  try {
    const key = userType === 'buyer' ? REMEMBER_ME_KEY_BUYER : REMEMBER_ME_KEY_SELLER;
    const preference = safeStorage.getItem(key);
    return preference === 'true';
  } catch (error) {
    console.error('Error getting remember me preference:', error);
    return false;
  }
};

/**
 * Detect if running in Lovable preview environment
 */
export const isLovablePreview = (): boolean => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('__lovable_token');
};

/**
 * Clear all auth-related storage (for logout and preview environment)
 * Uses safe storage wrappers for Safari compatibility
 */
export const clearAllAuthStorage = (): void => {
  try {
    const keysToRemove: string[] = [];
    
    // Use safe storage proxy for Safari compatibility
    const storage = createSafeStorageProxy(true);
    
    // Collect all auth-related keys from localStorage
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      // Preserve godyspo_session_id for trusted device functionality (30-day remember)
      if (key && key !== 'godyspo_session_id' && (
        key.includes('supabase') || 
        key.includes('auth') || 
        key.includes('session') ||
        key.includes('sb-') ||
        key.includes('godyspo_auth_')
      )) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all collected keys using safe storage
    keysToRemove.forEach(key => {
      safeStorage.removeItem(key);
    });
    
    // Also clear sessionStorage
    safeSessionStorage.clear();
    
    // Clear both seller and buyer remember me preferences
    safeStorage.removeItem(REMEMBER_ME_KEY_SELLER);
    safeStorage.removeItem(REMEMBER_ME_KEY_BUYER);
    safeStorage.removeItem(REMEMBERED_EMAIL_KEY_SELLER);
    safeStorage.removeItem(REMEMBERED_EMAIL_KEY_BUYER);
  } catch (error) {
    console.error('Error clearing auth storage:', error);
  }
};

/**
 * Save the user's email for "Remember me" functionality
 * Uses safe storage for Safari compatibility
 * @param email - The email to save
 * @param userType - 'seller' or 'buyer' to use type-specific storage keys
 */
export const saveRememberedEmail = (email: string, userType: 'seller' | 'buyer' = 'seller'): void => {
  try {
    const key = userType === 'buyer' ? REMEMBERED_EMAIL_KEY_BUYER : REMEMBERED_EMAIL_KEY_SELLER;
    safeStorage.setItem(key, email);
  } catch (error) {
    console.error('Error saving remembered email:', error);
  }
};

/**
 * Get the remembered email address
 * Uses safe storage for Safari compatibility
 * @param userType - 'seller' or 'buyer' to use type-specific storage keys
 */
export const getRememberedEmail = (userType: 'seller' | 'buyer' = 'seller'): string => {
  try {
    const key = userType === 'buyer' ? REMEMBERED_EMAIL_KEY_BUYER : REMEMBERED_EMAIL_KEY_SELLER;
    return safeStorage.getItem(key) || '';
  } catch (error) {
    console.error('Error getting remembered email:', error);
    return '';
  }
};

/**
 * Clear the remembered email address
 * Uses safe storage for Safari compatibility
 * @param userType - 'seller' or 'buyer' to use type-specific storage keys
 */
export const clearRememberedEmail = (userType: 'seller' | 'buyer' = 'seller'): void => {
  try {
    const key = userType === 'buyer' ? REMEMBERED_EMAIL_KEY_BUYER : REMEMBERED_EMAIL_KEY_SELLER;
    safeStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing remembered email:', error);
  }
};

// NOTE: Removed module-level authStorage.cleanup() call
// This was causing crashes in Safari Private Mode before ErrorBoundary was active
// Token cleanup is handled internally by Supabase and during auth operations
